import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { Partner } from 'src/app/models/partner.schema';
import { Product } from 'src/app/models/product.schema';
import { AuthStatusService } from 'src/app/services/auth-status.service';
import { PartnerService } from 'src/app/services/partner.service';
import { ProductService } from 'src/app/services/product.service';
import { ProvincesService } from 'src/app/services/provinces.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { BundleProductsService } from 'src/app/services/bundle-products.service';
import { CommanUsesService } from 'src/app/services/comman-uses.service';

@Component({
  selector: 'app-bundle-product-form',
  templateUrl: './bundle-product-form.component.html',
  styleUrls: ['./bundle-product-form.component.scss'],
})
export class BundleProductFormComponent implements OnInit {
  bundleProductId: string | null = null;

  groups: string[] = [];

  partners: Partner[] = [];
  provinces: any[] = [];

  products: Product[] = [];
  superProducts: Product[] = [];

  productsByPartner: Record<number, Product[]> = {};

  bundleProductForm!: FormGroup;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authStatusService: AuthStatusService,
    private partnerService: PartnerService,
    private provincesService: ProvincesService,
    private productService: ProductService,
    private spinnerService: SpinnerService,
    private bundleProductsService: BundleProductsService,
    private commanUsesService: CommanUsesService
  ) {
    this.groups = this.authStatusService
      .getRoles()
      .map((elem: string) => elem.toLowerCase());
  }

  async ngOnInit(): Promise<void> {
    this.spinnerService.show();

    this.bundleProductId = this.route.snapshot.paramMap.get('id');
    this.bundleProductForm = this.createBundleForm();

    try {
      const [partners, provinces, allProducts, superProducts] =
        await Promise.all([
          this.partnerService
            .getAllActivePartnersForDelivery()
            .then((res) => res.partners),

          this.provincesService.getAllProvinces(),

          this.productService
            .getAllActiveProductsForDelivery({})
            .then((res) => res.products),

          this.productService
            .getAllActiveProductsForDelivery({ isSuperProduct: true })
            .then((res) => res.products),
        ]);

      this.partners = partners || [];
      this.provinces = provinces || [];

      this.products = (allProducts || []).map((product: any) => ({
        ...product,
        displayName: this.getProductDisplayName(product),
      }));

      this.superProducts = (superProducts || []).map((product: any) => ({
        ...product,
        displayName: this.getProductDisplayName(product),
      }));

      this.buildProductsByPartner();

      if (this.bundleProductId) {
        const bundleProduct = await this.bundleProductsService.getOne(
          Number(this.bundleProductId)
        );

        this.patchBundleProduct(bundleProduct);
      } else {
        this.addComponent();
      }
    } catch (error: any) {
      await this.commanUsesService.showSnackBar(
        error?.error?.message || 'Errore durante il caricamento'
      );
    } finally {
      this.spinnerService.hide();
    }
  }

  createBundleForm(): FormGroup {
    return new FormGroup({
      product: new FormGroup({
        id: new FormControl(null, Validators.required),
      }),

      components: new FormArray([]),
    });
  }

  createComponentFormGroup(): FormGroup {
    const componentGroup = new FormGroup(
      {
        componentKey: new FormControl('', Validators.required),

        useSuperProvince: new FormControl(false),
        useUniqueProducts: new FormControl(false),

        superProvinceProduct: new FormGroup({
          id: new FormControl(null),
        }),

        uniqueProducts: new FormArray([]),
      },
      {
        validators: [this.componentSourcingValidator()],
      }
    );

    return componentGroup;
  }

  createUniqueProductFormGroup(): FormGroup {
    return new FormGroup({
      provinceId: new FormControl(null, Validators.required),

      partner: new FormGroup({
        id: new FormControl(null, Validators.required),
      }),

      product: new FormGroup({
        id: new FormControl(null, Validators.required),
      }),

      /**
       * UI-only field.
       * Used to prevent product filtering in the HTML.
       */
      availableProducts: new FormControl([]),
    });
  }

  componentSourcingValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;

      const useSuperProvince = group.get('useSuperProvince')?.value;
      const useUniqueProducts = group.get('useUniqueProducts')?.value;

      if (!useSuperProvince && !useUniqueProducts) {
        return {
          sourcingRequired: true,
        };
      }

      return null;
    };
  }

  get components(): FormArray {
    return this.bundleProductForm.get('components') as FormArray;
  }

  getUniqueProducts(componentIndex: number): FormArray {
    return this.components
      .at(componentIndex)
      .get('uniqueProducts') as FormArray;
  }

  addComponent(): void {
    this.components.push(this.createComponentFormGroup());
  }

  removeComponent(index: number): void {
    if (this.components.length === 1) {
      return;
    }

    this.components.removeAt(index);
  }

  addUniqueProduct(componentIndex: number): void {
    const componentGroup = this.components.at(componentIndex) as FormGroup;
    const uniqueProducts = this.getUniqueProducts(componentIndex);

    componentGroup.get('useUniqueProducts')?.setValue(true);

    uniqueProducts.push(this.createUniqueProductFormGroup());

    componentGroup.updateValueAndValidity();
  }

  removeUniqueProduct(componentIndex: number, rowIndex: number): void {
    const componentGroup = this.components.at(componentIndex) as FormGroup;
    const uniqueProducts = this.getUniqueProducts(componentIndex);

    uniqueProducts.removeAt(rowIndex);

    if (!uniqueProducts.length) {
      componentGroup.get('useUniqueProducts')?.setValue(false);
    }

    componentGroup.updateValueAndValidity();
  }

  handleSuperProvinceToggle(componentIndex: number): void {
    const componentGroup = this.components.at(componentIndex) as FormGroup;

    const useSuperProvince = componentGroup.get('useSuperProvince')?.value;
    const superProvinceProductId = componentGroup.get(
      'superProvinceProduct.id'
    );

    if (useSuperProvince) {
      superProvinceProductId?.setValidators([Validators.required]);
    } else {
      superProvinceProductId?.clearValidators();
      superProvinceProductId?.setValue(null);
    }

    superProvinceProductId?.updateValueAndValidity();
    componentGroup.updateValueAndValidity();
  }

  handleUniqueProductsToggle(componentIndex: number): void {
    const componentGroup = this.components.at(componentIndex) as FormGroup;

    const useUniqueProducts = componentGroup.get('useUniqueProducts')?.value;
    const uniqueProducts = this.getUniqueProducts(componentIndex);

    if (useUniqueProducts) {
      if (!uniqueProducts.length) {
        uniqueProducts.push(this.createUniqueProductFormGroup());
      }
    } else {
      uniqueProducts.clear();
    }

    componentGroup.updateValueAndValidity();
  }

  handlePartnerChange(componentIndex: number, rowIndex: number): void {
    const uniqueProductRow = this.getUniqueProducts(componentIndex).at(
      rowIndex
    ) as FormGroup;

    const partnerId = uniqueProductRow.get('partner.id')?.value;

    uniqueProductRow.get('product.id')?.setValue(null);

    const availableProducts = partnerId
      ? this.productsByPartner[Number(partnerId)] || []
      : [];

    uniqueProductRow.get('availableProducts')?.setValue(availableProducts);
  }

  buildProductsByPartner(): void {
    this.productsByPartner = {};

    this.products.forEach((product: any) => {
      const partnerId = this.getProductPartnerId(product);

      if (!partnerId) {
        return;
      }

      const id = Number(partnerId);

      if (!this.productsByPartner[id]) {
        this.productsByPartner[id] = [];
      }

      this.productsByPartner[id].push(product);
    });
  }

  getProductPartnerId(product: any): number | null {
    return (
      product.partner?.id ||
      product.partnerId ||
      product.partner_id ||
      product.deliveryPartner?.id ||
      product.delivery_partner_id ||
      null
    );
  }

  getProductDisplayName(product: any): string {
    return product?.isAlternateProductName && product?.alternateProductName
      ? product.alternateProductName
      : product?.name || '';
  }

  patchBundleProduct(bundleProduct: any): void {
    this.bundleProductForm.patchValue({
      product: {
        id: bundleProduct.product?.id || bundleProduct.product_id || null,
      },
    });

    this.components.clear();

    const components =
      bundleProduct.components ||
      bundleProduct.componentRules ||
      bundleProduct.bundleComponentRules ||
      [];

    if (!components.length) {
      this.addComponent();
      return;
    }

    components.forEach((component: any) => {
      const componentGroup = this.createComponentFormGroup();

      const componentKey =
        component.componentKey ||
        component.component_key ||
        component.key ||
        '';

      const superProvinceProductId =
        component.superProvinceProduct?.id ||
        component.super_province_product?.id ||
        component.superProvinceProductId ||
        component.super_province_product_id ||
        component.product?.id ||
        component.product_id ||
        null;

      const rows =
        component.uniqueProducts ||
        component.componentProvinces ||
        component.provinces ||
        [];

      const hasSuperProvince = !!superProvinceProductId;
      const hasUniqueProducts = rows.length > 0;

      componentGroup.patchValue({
        componentKey,
        useSuperProvince: hasSuperProvince,
        useUniqueProducts: hasUniqueProducts,
        superProvinceProduct: {
          id: superProvinceProductId,
        },
      });

      const superProvinceProductControl = componentGroup.get(
        'superProvinceProduct.id'
      );

      if (hasSuperProvince) {
        superProvinceProductControl?.setValidators([Validators.required]);
      } else {
        superProvinceProductControl?.clearValidators();
        superProvinceProductControl?.setValue(null);
      }

      superProvinceProductControl?.updateValueAndValidity();

      const uniqueProducts = componentGroup.get('uniqueProducts') as FormArray;
      uniqueProducts.clear();

      if (hasUniqueProducts) {
        rows.forEach((row: any) => {
          const partnerId =
            row.partner?.id ||
            row.partner_id ||
            row.partnerId ||
            null;

          const rowGroup = this.createUniqueProductFormGroup();

          rowGroup.patchValue({
            provinceId:
              row.province?.id ||
              row.province_id ||
              row.provinceId ||
              row.provinceCode ||
              row.province_code ||
              null,

            partner: {
              id: partnerId,
            },

            product: {
              id:
                row.product?.id ||
                row.product_id ||
                row.productId ||
                null,
            },

            availableProducts: partnerId
              ? this.productsByPartner[Number(partnerId)] || []
              : [],
          });

          uniqueProducts.push(rowGroup);
        });
      }

      componentGroup.updateValueAndValidity();

      this.components.push(componentGroup);
    });
  }

  buildPayload(): any {
    const rawValue = this.bundleProductForm.getRawValue();

    return {
      product_id: rawValue.product.id,

      components: rawValue.components.map((component: any) => {
        return {
          component_key: component.componentKey,

          /**
           * This is the global product for all provinces.
           * Null when this option is not enabled.
           */
          super_province_product_id: component.useSuperProvince
            ? component.superProvinceProduct.id
            : null,

          /**
           * This stores province + partner + product-specific mappings.
           * Empty when this option is not enabled.
           */
          provinces: component.useUniqueProducts
            ? component.uniqueProducts.map((row: any) => ({
                province_id: row.provinceId,
                partner_id: row.partner.id,
                product_id: row.product.id,
              }))
            : [],
        };
      }),
    };
  }

  async onSubmit(): Promise<void> {
    if (!this.bundleProductForm.valid) {
      this.bundleProductForm.markAllAsTouched();

      await this.commanUsesService.showSnackBar(
        'Compila tutti i campi obbligatori'
      );

      return;
    }

    try {
      this.spinnerService.show();

      const payload = this.buildPayload();
      const isEdit = !!this.bundleProductId;

      let response: any;

      if (isEdit) {
        response = await this.bundleProductsService.update(
          Number(this.bundleProductId),
          payload
        );

        await this.commanUsesService.showSnackBar(
          'Prodotto in bundle aggiornato'
        );

        await this.router.navigate([
          '/bundle/product/edit',
          this.bundleProductId,
        ]);
      } else {
        response = await this.bundleProductsService.create(payload);

        await this.commanUsesService.showSnackBar(
          'Prodotto in bundle creato'
        );

        await this.router.navigate(['/bundle/product/edit', response.id]);
      }
    } catch (error: any) {
      await this.commanUsesService.showSnackBar(
        error?.error?.message || 'Errore durante il salvataggio'
      );
    } finally {
      this.spinnerService.hide();
    }
  }

  getControlError(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  hasComponentSourcingError(componentForm: AbstractControl): boolean {
    return (
      !!componentForm.errors?.sourcingRequired &&
      (componentForm.dirty || componentForm.touched)
    );
  }
}